import * as RdxPopover from '@radix-ui/react-popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BADGE_COLOR_HEX_MAP,
  BADGE_COLOR_PASTEL_MAP,
  BadgeColorEnum,
} from '@clickersphere/core/enums/BadgeColorEnum';
import cn from '../../../app/utils/cn';
import ColorPalette from './ColorPalette';

interface IColorPickerProps {
  value: BadgeColorEnum;
  onChange: (color: BadgeColorEnum) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export default function ColorPicker({
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: IColorPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const triggerLabel = placeholder ?? t('badge.combobox.colorTrigger');

  function handleSelect(color: BadgeColorEnum) {
    onChange(color);
    setOpen(false);
  }

  return (
    <RdxPopover.Root open={open} onOpenChange={setOpen}>
      <RdxPopover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={triggerLabel}
          className={cn(
            'bg-surface-input border-border-input text-text-primary flex h-10 w-full items-center justify-between rounded border-2 border-solid px-3 text-sm shadow-md outline-none',
            'focus:border-green-600 dark:focus:border-green-400',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className="text-text-tertiary">{triggerLabel}</span>
          <span
            aria-hidden
            className="grid h-6 w-6 place-items-center rounded-full border border-white"
            style={{ backgroundColor: BADGE_COLOR_PASTEL_MAP[value] }}
          >
            <span
              className="block h-3 w-3 rounded-full"
              style={{ backgroundColor: BADGE_COLOR_HEX_MAP[value] }}
            />
          </span>
        </button>
      </RdxPopover.Trigger>

      <RdxPopover.Portal>
        <RdxPopover.Content className="z-50" sideOffset={6} align="end">
          <ColorPalette
            value={value}
            onChange={handleSelect}
            disabled={disabled}
          />
        </RdxPopover.Content>
      </RdxPopover.Portal>
    </RdxPopover.Root>
  );
}
